import { useEffect, useMemo, useRef, useState } from 'react'
import { Head, router, useForm, usePage } from '@inertiajs/react'
import { ChevronRight, GripVertical, Link as LinkIcon, MoreHorizontal, Plus, X } from 'lucide-react'
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Badge } from '@/shared/ui/badge'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu'
import { Button } from '@/shared/ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/shared/ui/collapsible'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table'
import { useCan } from '@/shared/hooks/useCan'
import { Resources, Actions } from '@/shared/rbac'
import { t } from '@/shared/lib/i18n'
import { cn } from '@/shared/lib/utils'
import {
  RICH_TEXT_DISPLAY_CLASSNAME,
  hasRichTextContent,
  sanitizeRichTextHtml,
} from '@/shared/lib/rich-text-html'
import {
  adminInstrumentTypesPath,
  adminInstrumentTypePlanningTaskPath,
  finishPlanningAdminInstrumentTypePath,
  lotAdminInstrumentTypeControlsReagentsPath,
  planningIdentityAdminInstrumentTypePath,
  reorderAdminInstrumentTypeMaintenanceTasksPath,
  updateTagsAdminInstrumentTypePlanningTaskPath,
} from '@/shared/lib/routes'
import {
  ControlsReagentsModal,
  type ControlsReagentsPersistPayload,
  type ControlsReagentsRow,
} from '@/pages/Admin/InstrumentTypes/ControlsReagentsModal'
import {
  MaintenanceTaskModal,
  type MaintenanceTaskPersistPayload,
} from '@/pages/Admin/InstrumentTypes/MaintenanceTaskModal'
import InstrumentTagCombobox from '@/shared/components/InstrumentTagCombobox'
import {
  InstrumentFormDialog,
  type InstrumentFormOptions,
} from '@/features/instrument-form/InstrumentFormDialog'
import { useBreadcrumbs } from '@/shared/lib/breadcrumbs-context'
import { formatIntervalOptionLabel } from '@/shared/lib/frequency/format-interval-option-label'
import { formatWallClockTime } from '@/shared/ui/wall-clock-input-utils'
import type { WeekdaySchedule } from '@/shared/ui/weekly-schedule-grid'
import type { SharedProps } from '@/shared/types/SharedProps'
import { Paperclip } from 'lucide-react'
import {
  InstrumentTypeAttachmentsManager,
  type InstrumentTypeAttachmentsManagerHandle,
} from '@/features/instrument-type-attachments/InstrumentTypeAttachmentsManager'
import { InstrumentTypeImageUploadSection } from '@/pages/Admin/InstrumentTypes/InstrumentTypeImageUploadSection'
import type {
  WebAdminInstrumentTypeMaintenanceTaskRow,
  WebAdminInstrumentTypePlanningLotRow,
  WebFrequencyType,
  WebInstrumentTaskIntervalType,
  WebInstrumentsTaskDataFieldTypeOption,
  WebInstrumentsTaskDataFieldValidationTypeOption,
} from '@/shared/types/web'

interface Summary {
  id: number | null
  name: string
  manufacturerText: string | null
  modelNumberText: string | null
  manufacturerDisplay: string | null
  modelNumberDisplay: string | null
  hasImage: boolean
  imagePath: string | null
}

type PlanningTaskRow = WebAdminInstrumentTypePlanningLotRow

type DraftControlsEntry = {
  display: PlanningTaskRow
  payload: ControlsReagentsPersistPayload
}

type DraftMaintenanceEntry = {
  display: WebAdminInstrumentTypeMaintenanceTaskRow
  payload: MaintenanceTaskPersistPayload
}

/** Drops a removed draft supply from a draft maintenance task's Task Links / Uses Lots. */
const clearDraftLotReference = (
  entry: DraftMaintenanceEntry,
  lotId: number,
): DraftMaintenanceEntry => {
  const { maintenanceTask } = entry.payload
  const usedLotIds = Array.isArray(maintenanceTask.usedLotIds) ? maintenanceTask.usedLotIds : []
  return {
    display: {
      ...entry.display,
      lotId: entry.display.lotId === lotId ? null : entry.display.lotId,
      usedLotIds: entry.display.usedLotIds.filter((id) => id !== lotId),
    },
    payload: {
      maintenanceTask: {
        ...maintenanceTask,
        lotId: Number(maintenanceTask.lotId) === lotId ? '' : maintenanceTask.lotId,
        usedLotIds: usedLotIds.filter((id) => Number(id) !== lotId),
      },
    },
  }
}

interface LotOption {
  id: number
  label: string
}

interface FormErrors {
  [key: string]: string[] | undefined
}

interface PageProps {
  summary: Summary
  controlsReagentsTasks: PlanningTaskRow[]
  maintenanceTasks: WebAdminInstrumentTypeMaintenanceTaskRow[]
  lotOptions: LotOption[]
  frequencyTypeOptions: WebFrequencyType[]
  intervalTypeOptions: WebInstrumentTaskIntervalType[]
  dataFieldTypeOptions: WebInstrumentsTaskDataFieldTypeOption[]
  dataFieldValidationTypeOptions: WebInstrumentsTaskDataFieldValidationTypeOption[]
  instrumentFormOptions: InstrumentFormOptions
  parentDaySchedule?: WeekdaySchedule
  isEdit?: boolean
  isDraftCreate?: boolean
  errors?: FormErrors
  [key: string]: unknown
}

const FieldError = ({ errors, field }: { errors: FormErrors; field: string }) => {
  const messages = errors[field]
  if (!messages?.length) return null
  return <p className="text-sm text-destructive mt-1">{messages[0]}</p>
}

const mergedPlanningFieldErrors = (
  pageErrors: FormErrors | undefined,
  formErrors: FormErrors,
): FormErrors => ({
  ...formErrors,
  ...(pageErrors ?? {}),
})

const IdentityBlock = ({
  summary,
  draftCreate = false,
}: {
  summary: Summary
  draftCreate?: boolean
}) => {
  const { errors: pageErrors } = usePage<{ errors?: FormErrors }>().props
  const canUpdate = useCan(Resources.ADMIN_INSTRUMENT_TYPE, Actions.UPDATE)
  const [editing, setEditing] = useState(false)
  const form = useForm({
    name: summary.name,
    manufacturerText: summary.manufacturerText ?? '',
    modelNumberText: summary.modelNumberText ?? '',
  })

  const save = () => {
    if (summary.id === null) return
    form.transform((data) => ({
      instrument_type: {
        name: data.name,
        manufacturer_text: data.manufacturerText.trim(),
        model_number_text: data.modelNumberText.trim(),
      },
    }))
    form.patch(planningIdentityAdminInstrumentTypePath(summary.id), {
      preserveScroll: true,
      onSuccess: () => setEditing(false),
    })
  }

  const cancel = () => {
    form.setData({
      name: summary.name,
      manufacturerText: summary.manufacturerText ?? '',
      modelNumberText: summary.modelNumberText ?? '',
    })
    setEditing(false)
  }

  const fieldErrors = mergedPlanningFieldErrors(pageErrors, form.errors as FormErrors)

  return (
    <section className="rounded-lg border p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">
          {t('admin.instrumentTypes.planning.identityTitle')}
        </h2>
        {!editing && canUpdate && !draftCreate && summary.id !== null && (
          <Button type="button" variant="outline" size="sm" onClick={() => setEditing(true)}>
            {t('admin.instrumentTypes.planning.editButton')}
          </Button>
        )}
      </div>

      {editing ? (
        <div className="space-y-3 max-w-xl">
          <div className="space-y-1.5">
            <Label htmlFor="identity-name">{t('admin.instrumentTypes.nameField')} *</Label>
            <Input
              id="identity-name"
              value={form.data.name}
              onChange={(event) => form.setData('name', event.target.value)}
              placeholder={t('admin.instrumentTypes.namePlaceholder')}
            />
            <FieldError errors={fieldErrors} field="name" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="identity-mfr">{t('admin.instrumentTypes.manufacturerTextField')}</Label>
            <Input
              id="identity-mfr"
              value={form.data.manufacturerText}
              onChange={(event) => form.setData('manufacturerText', event.target.value)}
              placeholder={t('admin.instrumentTypes.manufacturerTextPlaceholder')}
            />
            <FieldError errors={fieldErrors} field="manufacturer_text" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="identity-model">
              {t('admin.instrumentTypes.modelNumberTextField')}
            </Label>
            <Input
              id="identity-model"
              value={form.data.modelNumberText}
              onChange={(event) => form.setData('modelNumberText', event.target.value)}
              placeholder={t('admin.instrumentTypes.modelNumberTextPlaceholder')}
            />
            <FieldError errors={fieldErrors} field="model_number_text" />
          </div>
          <div className="flex gap-2">
            <Button type="button" size="sm" onClick={save} disabled={form.processing}>
              {t('admin.instrumentTypes.planning.saveButton')}
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={cancel}>
              {t('admin.instrumentTypes.planning.cancelButton')}
            </Button>
          </div>
        </div>
      ) : (
        <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-muted-foreground">{t('admin.instrumentTypes.nameField')}</dt>
            <dd className="font-medium">{summary.name}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">
              {t('admin.instrumentTypes.manufacturerTextField')}
            </dt>
            <dd className="font-medium">
              {summary.manufacturerDisplay ?? t('admin.instrumentTypes.planning.noneLabel')}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">
              {t('admin.instrumentTypes.modelNumberTextField')}
            </dt>
            <dd className="font-medium">
              {summary.modelNumberDisplay ?? t('admin.instrumentTypes.planning.noneLabel')}
            </dd>
          </div>
        </dl>
      )}
    </section>
  )
}

const ControlsReagentsSection = ({
  rows,
  typeId,
  lotOptions,
  draftMode = false,
  onDraftSave,
  onDraftRemove,
}: {
  rows: PlanningTaskRow[]
  typeId: number | null
  lotOptions: LotOption[]
  draftMode?: boolean
  onDraftSave?: (payload: ControlsReagentsPersistPayload, displayRow: ControlsReagentsRow) => void
  onDraftRemove?: (lotId: number) => void
}) => {
  const canCreate = useCan(Resources.ADMIN_INSTRUMENT_TYPE, Actions.CREATE)
  const canUpdate = useCan(Resources.ADMIN_INSTRUMENT_TYPE, Actions.UPDATE)
  const canDelete = useCan(Resources.ADMIN_INSTRUMENT_TYPE, Actions.DELETE)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingRow, setEditingRow] = useState<PlanningTaskRow | null>(null)
  const [deletingLotId, setDeletingLotId] = useState<number | null>(null)
  const [removeTargetRow, setRemoveTargetRow] = useState<PlanningTaskRow | null>(null)

  const assignedCatalogLotIds = useMemo(
    () => new Set(rows.map((planningTaskRow) => planningTaskRow.lotId)),
    [rows],
  )

  const selectableCatalogLots = useMemo(
    () => lotOptions.filter((catalogLot) => !assignedCatalogLotIds.has(catalogLot.id)),
    [lotOptions, assignedCatalogLotIds],
  )

  const openAdd = () => {
    setEditingRow(null)
    setModalOpen(true)
  }

  const openEdit = (row: PlanningTaskRow) => {
    setEditingRow(row)
    setModalOpen(true)
  }

  // Legacy parity (adminAddInstrumentType removeLot): a lot that is not linked to a scheduled task
  // can always be removed, whether or not it has a backing not_scheduled requirement task. Removal
  // is keyed on the catalog lot id and cascades to all related entities on the server (REBUILD-310).
  const confirmRemoveRow = (row: PlanningTaskRow) => {
    setRemoveTargetRow(null)
    if (draftMode) {
      onDraftRemove?.(row.lotId)
      return
    }
    if (typeId === null) return
    setDeletingLotId(row.lotId)
    router.delete(lotAdminInstrumentTypeControlsReagentsPath(typeId, row.lotId), {
      preserveScroll: true,
      onFinish: () => setDeletingLotId(null),
    })
  }

  const count = rows.length

  return (
    <section className="rounded-lg border p-4 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">
          {t('admin.instrumentTypes.planning.controlsSectionTitle')} ({count})
        </h2>
        {canCreate && (
          <Button type="button" size="sm" onClick={openAdd}>
            {t('admin.instrumentTypes.planning.addControlsReagents')}
          </Button>
        )}
      </div>

      {rows.length === 0 ? (
        <p className="py-4 text-sm text-muted-foreground">
          {t('admin.instrumentTypes.planning.noneLabel')}
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('admin.instrumentTypes.planning.taskNameLabel')}</TableHead>
              <TableHead className="w-56 text-right">
                {t('admin.instrumentTypes.planning.rowActions')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((planningRow) => (
              <TableRow key={planningRow.lotId}>
                <TableCell>
                  <div className="space-y-0.5">
                    <span>{planningRow.name}</span>
                    {planningRow.isLinkedToTask && (
                      <p className="text-xs text-muted-foreground">
                        <LinkIcon className="mr-1 inline h-3 w-3 shrink-0" aria-hidden />
                        {t('admin.instrumentTypes.planning.linkedToTask')}:{' '}
                        {planningRow.linkedTaskName}
                      </p>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2 flex-wrap">
                    {canUpdate && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => openEdit(planningRow)}
                        disabled={deletingLotId === planningRow.lotId}
                      >
                        {t('admin.instrumentTypes.controlsModal.editAction')}
                      </Button>
                    )}
                    {canDelete && !planningRow.isLinkedToTask && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setRemoveTargetRow(planningRow)}
                        disabled={deletingLotId === planningRow.lotId}
                      >
                        <X className="mr-1 h-4 w-4 shrink-0" aria-hidden />
                        {t('admin.instrumentTypes.planning.removeButton')}
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <ControlsReagentsModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        typeId={typeId}
        editingRow={editingRow}
        selectableCatalogLots={selectableCatalogLots}
        draftMode={draftMode}
        onLocalSave={onDraftSave}
      />

      <AlertDialog
        open={removeTargetRow !== null}
        onOpenChange={(open) => !open && setRemoveTargetRow(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('admin.instrumentTypes.planning.removeSupplierConfirmTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('admin.instrumentTypes.planning.removeReagentControlConfirm')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t('admin.instrumentTypes.planning.removeCancelButton')}
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => removeTargetRow !== null && confirmRemoveRow(removeTargetRow)}
            >
              {t('admin.instrumentTypes.planning.removeConfirmButton')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  )
}

const arraysEqualById = (a: number[], b: number[]) =>
  a.length === b.length &&
  [...a].sort((x, y) => x - y).every((v, i) => v === [...b].sort((x, y) => x - y)[i])

const FREQUENCY_TYPE_CALENDAR_FIXED = 'CALENDAR_FIXED'

const MaintenanceTaskRow = ({
  row,
  typeId,
  frequencyTypeOptions,
  intervalTypeOptions,
  dataFieldTypeOptions,
  dataFieldValidationTypeOptions,
  canUpdate,
  canCreate,
  canDelete,
  canReorder,
  deletingRowId,
  onEdit,
  onClone,
  onRemove,
}: {
  row: WebAdminInstrumentTypeMaintenanceTaskRow
  typeId: number
  frequencyTypeOptions: WebFrequencyType[]
  intervalTypeOptions: WebInstrumentTaskIntervalType[]
  dataFieldTypeOptions: WebInstrumentsTaskDataFieldTypeOption[]
  dataFieldValidationTypeOptions: WebInstrumentsTaskDataFieldValidationTypeOption[]
  canUpdate: boolean
  canCreate: boolean
  canDelete: boolean
  canReorder: boolean
  deletingRowId: number | null
  onEdit: () => void
  onClone: () => void
  onRemove: () => void
}) => {
  const canEditTaskTags = useCan(Resources.ADMIN_INSTRUMENT_TYPE, Actions.UPDATE)
  const { tenantTimeFormat } = usePage<SharedProps>().props

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.id,
    disabled: !canReorder,
  })

  const sortableStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
  }

  const [open, setOpen] = useState(false)
  const initialTagIds = row.tags.map((tag) => tag.id)
  const [tagIds, setTagIds] = useState<number[]>(initialTagIds)
  const [savingTags, setSavingTags] = useState(false)

  const tagsDirty = !arraysEqualById(tagIds, initialTagIds)

  const frequencyType = frequencyTypeOptions.find((ft) => ft.id === row.frequencyTypeId)
  const intervalType = intervalTypeOptions.find((it) => it.id === row.instrumentTaskIntervalTypeId)
  const isCalendarFixed = intervalType?.frequencyTypeCode === FREQUENCY_TYPE_CALENDAR_FIXED

  const saveTags = () => {
    setSavingTags(true)
    router.patch(
      updateTagsAdminInstrumentTypePlanningTaskPath(typeId, row.id),
      { planning_task: { tag_ids: tagIds } },
      { preserveScroll: true, onFinish: () => setSavingTags(false) },
    )
  }

  const isDeleting = deletingRowId === row.id

  return (
    <Collapsible
      ref={setNodeRef}
      style={sortableStyle}
      open={open}
      onOpenChange={setOpen}
      // REBUILD-322: the open task is one gray block with a left edge so it
      // stands out from the white closed rows beside it.
      className={cn(
        'rounded-md border bg-card',
        open && 'border-l-4 border-l-gray-400 bg-gray-100',
        isDragging && 'shadow-lg',
      )}
      {...attributes}
    >
      <div className="flex items-center gap-2 px-3 py-2.5">
        {canReorder && (
          <button
            type="button"
            className="shrink-0 cursor-grab touch-none p-0.5 text-muted-foreground hover:text-foreground active:cursor-grabbing"
            aria-label={t('admin.instrumentTypes.planning.dragHandleLabel')}
            {...listeners}
          >
            <GripVertical className="h-4 w-4" />
          </button>
        )}
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="shrink-0 p-0.5 text-muted-foreground hover:text-foreground"
            aria-label={open ? 'Collapse' : 'Expand'}
          >
            <ChevronRight
              className={`h-4 w-4 transition-transform duration-150 ${open ? 'rotate-90' : ''}`}
            />
          </button>
        </CollapsibleTrigger>
        <span className="flex-1 min-w-0 text-sm font-medium truncate">{row.name}</span>

        {/* Desktop: individual buttons */}
        <div className="hidden sm:flex gap-2 flex-nowrap">
          {canUpdate && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={onEdit}
              disabled={isDeleting}
            >
              {row.isAllowEdit
                ? t('admin.instrumentTypes.maintenanceModal.editAction')
                : t('admin.instrumentTypes.maintenanceModal.updateAction')}
            </Button>
          )}
          {canCreate && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={onClone}
              disabled={isDeleting}
            >
              {t('admin.instrumentTypes.maintenanceModal.cloneAction')}
            </Button>
          )}
          {canDelete && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={onRemove}
              disabled={isDeleting}
            >
              <X className="mr-1 h-4 w-4 shrink-0" aria-hidden />
              {t('admin.instrumentTypes.planning.removeButton')}
            </Button>
          )}
        </div>

        {/* Mobile: dropdown menu */}
        {(canUpdate || canCreate || canDelete) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="sm:hidden shrink-0 px-2"
                disabled={isDeleting}
                aria-label="Task actions"
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {canUpdate && (
                <DropdownMenuItem onSelect={onEdit}>
                  {row.isAllowEdit
                    ? t('admin.instrumentTypes.maintenanceModal.editAction')
                    : t('admin.instrumentTypes.maintenanceModal.updateAction')}
                </DropdownMenuItem>
              )}
              {canCreate && (
                <DropdownMenuItem onSelect={onClone}>
                  {t('admin.instrumentTypes.maintenanceModal.cloneAction')}
                </DropdownMenuItem>
              )}
              {canDelete && (
                <DropdownMenuItem onSelect={onRemove} className="text-destructive">
                  {t('admin.instrumentTypes.planning.removeButton')}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <CollapsibleContent>
        <div className="border-t px-4 py-3 space-y-3">
          <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-muted-foreground">
                {t('admin.instrumentTypes.planning.taskFrequencyTypeLabel')}
              </dt>
              <dd className="font-medium">
                {frequencyType ? frequencyType.name : t('admin.instrumentTypes.planning.noneLabel')}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">
                {t('admin.instrumentTypes.planning.taskFrequencyLabel')}
              </dt>
              <dd className="font-medium">
                {intervalType
                  ? formatIntervalOptionLabel(intervalType, intervalTypeOptions)
                  : t('admin.instrumentTypes.planning.noneLabel')}
              </dd>
            </div>
            {isCalendarFixed && (
              <>
                <div>
                  <dt className="text-muted-foreground">
                    {t('admin.instrumentTypes.planning.taskStartAtLabel')}
                  </dt>
                  <dd className="font-medium">
                    {formatWallClockTime(row.fixedScheduleStartTime || '00:00', tenantTimeFormat)}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">
                    {t('admin.instrumentTypes.planning.taskEndsAtMidnightLabel')}
                  </dt>
                  <dd className="font-medium">
                    {row.endsAtMidnight
                      ? t('admin.instrumentTypes.planning.yes')
                      : t('admin.instrumentTypes.planning.no')}
                  </dd>
                </div>
              </>
            )}
            <div>
              <dt className="text-muted-foreground">
                {t('admin.instrumentTypes.planning.taskAffectsComplianceLabel')}
              </dt>
              <dd className="font-medium">
                {row.affectsCompliance
                  ? t('admin.instrumentTypes.planning.yes')
                  : t('admin.instrumentTypes.planning.no')}
              </dd>
            </div>
            {hasRichTextContent(row.notes) && (
              <div className="sm:col-span-2">
                <dt className="text-muted-foreground">
                  {t('admin.instrumentTypes.planning.taskNotesLabel')}
                </dt>
                {/* Task notes come from RichTextEditor, so they hold HTML (REBUILD-356). */}
                <dd
                  className={cn(RICH_TEXT_DISPLAY_CLASSNAME, 'font-medium [&_p]:my-0')}
                  dangerouslySetInnerHTML={{ __html: sanitizeRichTextHtml(row.notes ?? '') }}
                />
              </div>
            )}
          </dl>

          {row.dataFields.length > 0 && (
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">
                {t('admin.instrumentTypes.planning.taskDataFieldsLabel')}
              </p>
              <ul className="space-y-0.5 text-sm">
                {row.dataFields.map((field) => {
                  const fieldType = dataFieldTypeOptions.find(
                    (opt) => opt.id === field.instrumentTaskDataFieldTypeId,
                  )
                  const isDefinedType = fieldType?.dataFieldType === 'defined'
                  const acceptableValueRows = field.acceptableValueRows as {
                    id: number
                    value: string | null
                    isAcceptable: boolean
                    active: boolean
                    instrumentTaskDataFieldValidationTypeId: number
                    instrumentTaskDefinedValueId: number | null
                  }[]
                  const definedValueRows = field.definedValueRows as {
                    id: number
                    value: string
                  }[]
                  const activeRules = acceptableValueRows.filter((r) => r.active)

                  const acceptableDefinedLabels = isDefinedType
                    ? activeRules
                        .filter((r) => r.isAcceptable)
                        .map((r) => {
                          const definedRow = definedValueRows.find(
                            (dv) => dv.id === r.instrumentTaskDefinedValueId,
                          )
                          return definedRow?.value ?? r.value
                        })
                        .filter(Boolean)
                    : []

                  return (
                    <li key={field.id} className="flex items-center gap-2 text-sm flex-wrap">
                      <span className="font-medium">{field.label}</span>
                      {fieldType && (
                        <span className="text-muted-foreground">
                          : {fieldType.dataFieldTypeDesc}
                        </span>
                      )}
                      {field.required && (
                        <span className="text-xs text-muted-foreground">
                          ({t('admin.instrumentTypes.maintenanceModal.required').toLowerCase()})
                        </span>
                      )}
                      {isDefinedType
                        ? acceptableDefinedLabels.length > 0 && (
                            <span className="text-xs text-destructive">
                              (acceptable: {acceptableDefinedLabels.join(', ')})
                            </span>
                          )
                        : activeRules.map((rule) => {
                            const validationType = dataFieldValidationTypeOptions.find(
                              (opt) => opt.id === rule.instrumentTaskDataFieldValidationTypeId,
                            )
                            return (
                              <span key={rule.id} className="text-xs text-destructive">
                                ({validationType?.validationTypeDesc}: {rule.value})
                              </span>
                            )
                          })}
                    </li>
                  )
                })}
              </ul>
            </div>
          )}

          <div className="space-y-1.5">
            <p className="text-sm text-muted-foreground">
              {t('admin.instrumentTypes.planning.taskTagsLabel')}
            </p>
            {canEditTaskTags ? (
              <>
                <InstrumentTagCombobox
                  selectedIds={tagIds}
                  onChange={setTagIds}
                  initialTags={row.tags.map((tag) => ({ value: tag.id, label: tag.name }))}
                />
                {tagsDirty && (
                  <Button type="button" size="sm" onClick={saveTags} disabled={savingTags}>
                    {t('admin.instrumentTypes.planning.taskSaveTagsButton')}
                  </Button>
                )}
              </>
            ) : (
              <div className="flex flex-wrap gap-1">
                {row.tags.length === 0 ? (
                  <span className="text-sm text-muted-foreground">
                    {t('admin.instrumentTypes.planning.noneLabel')}
                  </span>
                ) : (
                  row.tags.map((tag) => (
                    <Badge key={tag.id} variant="secondary">
                      {tag.name}
                    </Badge>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}

const MaintenanceSection = ({
  rows,
  typeId,
  lotOptions,
  frequencyTypeOptions,
  intervalTypeOptions,
  dataFieldTypeOptions,
  dataFieldValidationTypeOptions,
  draftMode = false,
  onDraftSave,
  onDraftRemove,
}: {
  rows: WebAdminInstrumentTypeMaintenanceTaskRow[]
  typeId: number | null
  lotOptions: LotOption[]
  frequencyTypeOptions: WebFrequencyType[]
  intervalTypeOptions: WebInstrumentTaskIntervalType[]
  dataFieldTypeOptions: WebInstrumentsTaskDataFieldTypeOption[]
  dataFieldValidationTypeOptions: WebInstrumentsTaskDataFieldValidationTypeOption[]
  draftMode?: boolean
  onDraftSave?: (
    payload: MaintenanceTaskPersistPayload,
    displayRow: WebAdminInstrumentTypeMaintenanceTaskRow,
  ) => void
  onDraftRemove?: (taskId: number) => void
}) => {
  const canCreate = useCan(Resources.ADMIN_INSTRUMENT_TYPE, Actions.CREATE)
  const canUpdate = useCan(Resources.ADMIN_INSTRUMENT_TYPE, Actions.UPDATE)
  const canDelete = useCan(Resources.ADMIN_INSTRUMENT_TYPE, Actions.DELETE)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingRow, setEditingRow] = useState<WebAdminInstrumentTypeMaintenanceTaskRow | null>(
    null,
  )
  const [cloningRow, setCloningRow] = useState<WebAdminInstrumentTypeMaintenanceTaskRow | null>(
    null,
  )
  const [deletingRowId, setDeletingRowId] = useState<number | null>(null)
  const [removeTargetId, setRemoveTargetId] = useState<number | null>(null)
  const [lockedEditRow, setLockedEditRow] =
    useState<WebAdminInstrumentTypeMaintenanceTaskRow | null>(null)
  const [orderedRows, setOrderedRows] = useState<WebAdminInstrumentTypeMaintenanceTaskRow[]>(rows)

  // Keep the local drag order in sync when the server props change (add/edit/remove).
  useEffect(() => {
    setOrderedRows(rows)
  }, [rows])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const canReorder = !draftMode && canUpdate && orderedRows.length > 1 && typeId !== null

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id || typeId === null || draftMode) return

    const oldIndex = orderedRows.findIndex((row) => row.id === active.id)
    const newIndex = orderedRows.findIndex((row) => row.id === over.id)
    if (oldIndex === -1 || newIndex === -1) return

    const reordered = arrayMove(orderedRows, oldIndex, newIndex)
    setOrderedRows(reordered)

    router.patch(
      reorderAdminInstrumentTypeMaintenanceTasksPath(typeId),
      { task_ids: reordered.map((row) => row.id) },
      { preserveScroll: true, preserveState: true },
    )
  }

  const openAdd = () => {
    setEditingRow(null)
    setCloningRow(null)
    setModalOpen(true)
  }

  // Legacy G1: an executed task (Update) with open non-compliance is fully locked —
  // show a blocking dialog instead of opening the editor.
  const openEdit = (row: WebAdminInstrumentTypeMaintenanceTaskRow) => {
    if (!row.isAllowEdit && row.openNonComplianceCount > 0) {
      setLockedEditRow(row)
      return
    }

    setEditingRow(row)
    setCloningRow(null)
    setModalOpen(true)
  }

  const openClone = (row: WebAdminInstrumentTypeMaintenanceTaskRow) => {
    setCloningRow(row)
    setEditingRow(null)
    setModalOpen(true)
  }

  const confirmRemoveRow = (rowId: number) => {
    setRemoveTargetId(null)
    if (draftMode) {
      onDraftRemove?.(rowId)
      return
    }
    if (typeId === null) return
    setDeletingRowId(rowId)
    router.delete(adminInstrumentTypePlanningTaskPath(typeId, rowId), {
      preserveScroll: true,
      onFinish: () => setDeletingRowId(null),
    })
  }

  const lockedDepartmentNames = lockedEditRow?.openNonComplianceDepartmentNames ?? []
  const lockedDepartmentsLabel =
    lockedDepartmentNames.length > 1
      ? `${lockedDepartmentNames.slice(0, -1).join(', ')} and ${lockedDepartmentNames.at(-1)}`
      : (lockedDepartmentNames[0] ?? '')

  return (
    <section className="rounded-lg border p-4 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">
          {t('admin.instrumentTypes.planning.maintenanceSectionTitle')} ({rows.length})
        </h2>
        {canCreate && (
          <Button type="button" size="sm" onClick={openAdd}>
            {t('admin.instrumentTypes.planning.addMaintenanceTask')}
          </Button>
        )}
      </div>

      {orderedRows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t('admin.instrumentTypes.planning.noneLabel')}
        </p>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={orderedRows.map((planningRow) => planningRow.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-2">
              {orderedRows.map((planningRow) => (
                <MaintenanceTaskRow
                  key={planningRow.id}
                  row={planningRow}
                  typeId={typeId ?? 0}
                  frequencyTypeOptions={frequencyTypeOptions}
                  intervalTypeOptions={intervalTypeOptions}
                  dataFieldTypeOptions={dataFieldTypeOptions}
                  dataFieldValidationTypeOptions={dataFieldValidationTypeOptions}
                  canUpdate={canUpdate}
                  canCreate={canCreate && !draftMode}
                  canDelete={canDelete}
                  canReorder={canReorder}
                  deletingRowId={deletingRowId}
                  onEdit={() => openEdit(planningRow)}
                  onClone={() => openClone(planningRow)}
                  onRemove={() => setRemoveTargetId(planningRow.id)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      <MaintenanceTaskModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        typeId={typeId}
        editingRow={editingRow}
        cloneFromRow={cloningRow}
        frequencyTypeOptions={frequencyTypeOptions}
        intervalTypeOptions={intervalTypeOptions}
        dataFieldTypeOptions={dataFieldTypeOptions}
        dataFieldValidationTypeOptions={dataFieldValidationTypeOptions}
        lotOptions={lotOptions}
        draftMode={draftMode}
        onLocalSave={onDraftSave}
      />

      <AlertDialog
        open={removeTargetId !== null}
        onOpenChange={(open) => !open && setRemoveTargetId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('admin.instrumentTypes.planning.removeConfirmTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                orderedRows.find((row) => row.id === removeTargetId)?.isAllowEdit
                  ? 'admin.instrumentTypes.planning.removeConfirmNeverCompleted'
                  : 'admin.instrumentTypes.planning.removeConfirm',
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t('admin.instrumentTypes.planning.removeCancelButton')}
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => removeTargetId !== null && confirmRemoveRow(removeTargetId)}
            >
              {t('admin.instrumentTypes.planning.removeConfirmButton')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={lockedEditRow !== null}
        onOpenChange={(open) => !open && setLockedEditRow(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('admin.instrumentTypes.maintenanceModal.nonComplianceLockTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('admin.instrumentTypes.maintenanceModal.nonComplianceLockBody', {
                count: lockedEditRow?.openNonComplianceCount ?? 0,
                departments: lockedDepartmentsLabel,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setLockedEditRow(null)}>
              {t('admin.instrumentTypes.maintenanceModal.nonComplianceLockConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  )
}

const toPlanningControlsRow = (displayRow: ControlsReagentsRow): PlanningTaskRow => ({
  lotId: displayRow.lotId,
  name: displayRow.name,
  instrumentTaskId: displayRow.instrumentTaskId,
  requiresValidation: displayRow.requiresValidation,
  isKit: displayRow.isKit,
  notes: displayRow.notes,
  secondaryLots: displayRow.secondaryLots,
  linkedTaskName: null,
  isLinkedToTask: false,
  tags: [],
})

const PlanningPage = () => {
  const {
    summary,
    controlsReagentsTasks,
    maintenanceTasks,
    lotOptions,
    frequencyTypeOptions,
    intervalTypeOptions,
    dataFieldTypeOptions,
    dataFieldValidationTypeOptions,
    instrumentFormOptions,
    isEdit,
    isDraftCreate = false,
  } = usePage<PageProps>().props
  const { errors: pageErrors } = usePage<{ errors?: FormErrors }>().props
  const canUpdateType = useCan(Resources.ADMIN_INSTRUMENT_TYPE, Actions.UPDATE)
  const canCreateType = useCan(Resources.ADMIN_INSTRUMENT_TYPE, Actions.CREATE)
  const canFinishPlanning = isDraftCreate ? canCreateType : canUpdateType
  const canCreateInstrument = useCan(Resources.INSTRUMENT_MAINTENANCE, Actions.CREATE)
  const canCreateAttachment = useCan(Resources.ATTACHMENT, Actions.CREATE)
  const canDeleteAttachment = useCan(Resources.ATTACHMENT, Actions.DELETE)
  const [finishOpen, setFinishOpen] = useState(false)
  const [cancelOpen, setCancelOpen] = useState(false)
  const [addInstrumentOpen, setAddInstrumentOpen] = useState(false)
  const [finishing, setFinishing] = useState(false)
  const [finishError, setFinishError] = useState<string | null>(null)
  const [draftControls, setDraftControls] = useState<DraftControlsEntry[]>([])
  const [draftMaintenance, setDraftMaintenance] = useState<DraftMaintenanceEntry[]>([])
  const attachmentsManagerRef = useRef<InstrumentTypeAttachmentsManagerHandle>(null)

  useBreadcrumbs([
    { label: t('nav.administration') },
    { label: t('nav.instrumentTypes'), href: adminInstrumentTypesPath() },
    { label: t('admin.instrumentTypes.planning.pageTitle') },
  ])

  // Mirrors the server's linked state (Task Links only): a draft supply used as a draft task's
  // lot cannot be removed, matching legacy and the persisted flow.
  const visibleControlsRows = isDraftCreate
    ? draftControls.map((entry) => {
        const linkedTaskNames = draftMaintenance
          .filter((task) => task.display.lotId === entry.display.lotId)
          .map((task) => task.display.name ?? '')
        return linkedTaskNames.length === 0
          ? entry.display
          : {
              ...entry.display,
              isLinkedToTask: true,
              linkedTaskName: [...new Set(linkedTaskNames)].join(', '),
            }
      })
    : controlsReagentsTasks
  const visibleMaintenanceRows = isDraftCreate
    ? draftMaintenance.map((entry) => entry.display)
    : maintenanceTasks

  // Draft supplies have no backend row yet, so surface them alongside the catalog lots for
  // Task Links / Task Uses Lots. Supplies that reuse a catalog lot are already in lotOptions.
  const maintenanceLotOptions = useMemo<LotOption[]>(
    () =>
      isDraftCreate
        ? [
            ...lotOptions,
            ...draftControls
              .filter((entry) => !entry.payload.lot_id)
              .map((entry) => ({ id: entry.display.lotId, label: entry.display.name ?? '' })),
          ]
        : lotOptions,
    [isDraftCreate, lotOptions, draftControls],
  )

  const removeDraftControl = (lotId: number) => {
    setDraftControls((previous) => previous.filter((entry) => entry.display.lotId !== lotId))
    setDraftMaintenance((previous) => previous.map((entry) => clearDraftLotReference(entry, lotId)))
  }

  const omissions: string[] = []
  if (visibleControlsRows.length === 0) {
    omissions.push(t('admin.instrumentTypes.planning.omissionControls'))
  }
  if (visibleMaintenanceRows.length === 0) {
    omissions.push(t('admin.instrumentTypes.planning.omissionMaintenance'))
  }

  const confirmFinish = async () => {
    setFinishing(true)
    setFinishError(null)

    if (isDraftCreate) {
      router.post(
        adminInstrumentTypesPath(),
        {
          finish_wizard: true,
          instrument_type: {
            name: summary.name,
            manufacturer_text: summary.manufacturerText ?? '',
            model_number_text: summary.modelNumberText ?? '',
          },
          controls_reagents: draftControls.map((entry) => entry.payload),
          maintenance_tasks: draftMaintenance.map((entry) => entry.payload),
        },
        {
          onFinish: () => setFinishing(false),
          onError: () => setFinishError(t('admin.instrumentTypes.planning.finishError')),
        },
      )
      return
    }

    if (summary.id === null) {
      setFinishing(false)
      return
    }

    const result = await attachmentsManagerRef.current?.commitStagedChanges()
    if (result && result.errors.length > 0) {
      setFinishing(false)
      return
    }

    router.post(finishPlanningAdminInstrumentTypePath(summary.id), {})
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <Head title={t('admin.instrumentTypes.planning.pageTitle')} />
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-muted-foreground">
            {t('admin.instrumentTypes.wizardStepTwoLabel')}
          </p>
          <h1 className="text-2xl font-bold mt-1">
            {isEdit ? t('admin.instrumentTypes.editTitle') : t('admin.instrumentTypes.wizardTitle')}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {t('admin.instrumentTypes.planning.leadText')}
          </p>
        </div>
        {canCreateInstrument && !isDraftCreate && summary.id !== null && (
          <Button
            variant="outline"
            size="sm"
            className="shrink-0"
            onClick={() => setAddInstrumentOpen(true)}
          >
            <Plus className="h-4 w-4" />
            {t('admin.instrumentTypes.instrumentList.addInstrument')}
          </Button>
        )}
      </div>

      {canCreateInstrument && addInstrumentOpen && summary.id !== null && (
        <InstrumentFormDialog
          open={addInstrumentOpen}
          onOpenChange={setAddInstrumentOpen}
          instrumentTypeId={summary.id}
          instrumentTypeName={summary.name}
          options={instrumentFormOptions}
          returnTo="instrument_types"
        />
      )}

      {(pageErrors?.base?.length ?? 0) > 0 && (
        <div className="rounded border border-destructive bg-destructive/10 px-4 py-3">
          {pageErrors!.base!.map((message, index) => (
            <p key={index} className="text-sm text-destructive">
              {message}
            </p>
          ))}
        </div>
      )}

      <IdentityBlock summary={summary} draftCreate={isDraftCreate} />

      {!isDraftCreate && summary.id !== null && (
        <>
          <div className="max-w-xl">
            <InstrumentTypeImageUploadSection
              instrumentType={{
                id: summary.id,
                hasImage: summary.hasImage,
                imagePath: summary.imagePath,
              }}
            />
          </div>

          <section className="space-y-4 rounded-lg border p-4">
            <h2 className="flex items-center gap-2 text-lg font-semibold">
              <Paperclip className="h-4 w-4 text-muted-foreground" />
              {t('admin.instrumentTypes.attachments.sectionTitle')}
            </h2>
            <InstrumentTypeAttachmentsManager
              ref={attachmentsManagerRef}
              instrumentTypeId={summary.id}
              canCreate={canCreateAttachment}
              canDelete={canDeleteAttachment}
              staged
            />
          </section>
        </>
      )}

      <ControlsReagentsSection
        rows={visibleControlsRows}
        typeId={summary.id}
        lotOptions={lotOptions}
        draftMode={isDraftCreate}
        onDraftSave={(payload, displayRow) => {
          const planningRow = toPlanningControlsRow(displayRow)
          setDraftControls((previous) => {
            const existingIndex = previous.findIndex(
              (entry) => entry.display.lotId === planningRow.lotId,
            )
            if (existingIndex >= 0) {
              const next = [...previous]
              next[existingIndex] = { display: planningRow, payload }
              return next
            }
            return [...previous, { display: planningRow, payload }]
          })
        }}
        onDraftRemove={removeDraftControl}
      />

      <MaintenanceSection
        rows={visibleMaintenanceRows}
        typeId={summary.id}
        lotOptions={maintenanceLotOptions}
        frequencyTypeOptions={frequencyTypeOptions}
        intervalTypeOptions={intervalTypeOptions}
        dataFieldTypeOptions={dataFieldTypeOptions}
        dataFieldValidationTypeOptions={dataFieldValidationTypeOptions}
        draftMode={isDraftCreate}
        onDraftSave={(payload, displayRow) => {
          setDraftMaintenance((previous) => {
            const existingIndex = previous.findIndex((entry) => entry.display.id === displayRow.id)
            if (existingIndex >= 0) {
              const next = [...previous]
              next[existingIndex] = { display: displayRow, payload }
              return next
            }
            return [...previous, { display: displayRow, payload }]
          })
        }}
        onDraftRemove={(taskId) => {
          setDraftMaintenance((previous) => previous.filter((entry) => entry.display.id !== taskId))
        }}
      />

      {finishError && (
        <div className="rounded border border-destructive bg-destructive/10 px-4 py-3">
          <p className="text-sm text-destructive">{finishError}</p>
        </div>
      )}

      {canFinishPlanning && (
        <div className="flex gap-3">
          <Button
            type="button"
            disabled={finishing}
            onClick={() => (omissions.length > 0 ? setFinishOpen(true) : confirmFinish())}
          >
            {finishing
              ? t('admin.instrumentTypes.planning.finishing')
              : t('admin.instrumentTypes.planning.finishButton')}
          </Button>
          {isDraftCreate && (
            <Button
              type="button"
              variant="outline"
              disabled={finishing}
              onClick={() => setCancelOpen(true)}
            >
              {t('admin.instrumentTypes.planning.cancelButton')}
            </Button>
          )}
        </div>
      )}

      <Dialog open={finishOpen} onOpenChange={setFinishOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin.instrumentTypes.planning.finishConfirmTitle')}</DialogTitle>
          </DialogHeader>
          <div className="text-sm space-y-2">
            <p>{t('admin.instrumentTypes.planning.finishConfirmIntro')}</p>
            <ul className="list-disc pl-5 space-y-1">
              {omissions.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setFinishOpen(false)}>
              {t('admin.instrumentTypes.planning.cancelButton')}
            </Button>
            <Button
              type="button"
              disabled={finishing}
              onClick={() => {
                setFinishOpen(false)
                confirmFinish()
              }}
            >
              {t('admin.instrumentTypes.planning.finishConfirmButton')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* REBUILD-39: confirm before discarding in-memory create draft (no backend orphan) */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin.instrumentTypes.cancelCreateDialog.title')}</DialogTitle>
            <DialogDescription>
              {t('admin.instrumentTypes.cancelCreateDialog.body')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setCancelOpen(false)}>
              {t('admin.instrumentTypes.cancelCreateDialog.cancel')}
            </Button>
            <Button
              type="button"
              onClick={() => {
                setCancelOpen(false)
                router.visit(adminInstrumentTypesPath())
              }}
            >
              {t('admin.instrumentTypes.cancelCreateDialog.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default PlanningPage
